import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { router } from 'expo-router';
import { Camera, Home, ClipboardCheck, ArrowLeftRight, ChevronRight } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { Button, LoadingSpinner, EmptyState, useConfirm } from '../common';
import { PostCard } from './PostCard';
import { InlineComments } from './InlineComments';
import { usePostsStore } from '../../store/postsStore';
import { useAuthStore } from '../../store/authStore';
import { useAuthGate } from '../../hooks/useAuthGate';
import { useDesktopShell } from '../../hooks/useDesktopShell';
import { isPermissionDenied } from '../../utils/errorHandler';
import { normalizePostCategory } from '../../types';

export interface PostDetailBodyProps {
  postId: string;
  /** Mirrors the `/post/[id]` route's `openComments=1` query param: true
   *  scrolls the desktop thread into view once the post loads. The phone
   *  path (opening CommentsSheet) stays with whichever host owns that
   *  sheet's visibility state — see `onRequestComments`. */
  openComments?: boolean;
  /** Called when the user asks to view comments and this isn't the desktop
   *  shell (PostCard's comment icon) — the host opens its own CommentsSheet.
   *  Omit on a desktop-only host (the docked post panel never calls this). */
  onRequestComments?: () => void;
}

/**
 * Everything below a post's page chrome: the card, its property/checklist/
 * before-photo row-links, the after-photo prompt, and the desktop comment
 * thread. Shared by the `/post/[id]` route (phone, share links, non-map
 * screens) and the desktop map's right-docked post panel
 * (app/(tabs)/index.tsx's `dockedPostId` — panel-system.md Phase 2). Each
 * caller owns its own page chrome (Header, or the panel's title + close
 * button) and its own scroll container; this renders plain content, no
 * ScrollView of its own, and fetches+owns the post itself (both hosts read
 * the same `postsById` cache, so there's only ever one fetch in flight).
 */
export function PostDetailBody({ postId, openComments, onRequestComments }: PostDetailBodyProps) {
  const { colors } = useTheme();
  const isDesktopShell = useDesktopShell();
  const user = useAuthStore((s) => s.user);
  const { selectedPost, fetchPost, likedPosts, likePost, unlikePost, deletePost, checkLiked } =
    usePostsStore(
      useShallow((s) => ({
        selectedPost: postId ? (s.postsById[postId] ?? null) : null,
        fetchPost: s.fetchPost,
        likedPosts: s.likedPosts,
        likePost: s.likePost,
        unlikePost: s.unlikePost,
        deletePost: s.deletePost,
        checkLiked: s.checkLiked,
      }))
    );

  const confirm = useConfirm();
  const { requireAuth } = useAuthGate();
  // Anchor for the desktop "scroll to comments" behavior — scrollIntoView
  // walks up to whichever ancestor actually scrolls (the route's ScrollView,
  // or the docked panel's), so this component never needs to know which
  // host it's in.
  const commentsAnchorRef = useRef<View>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);

  useEffect(() => {
    if (!postId) return;
    setFetchFailed(false);
    setAccessDenied(false);
    fetchPost(postId)
      .then((post) => {
        if (!post) setFetchFailed(true);
      })
      .catch((error) => {
        setAccessDenied(isPermissionDenied(error));
        setFetchFailed(true);
      });
  }, [postId, fetchPost]);

  // Matches the feed's batch like-check: opened directly (not via the feed),
  // `likedPosts` hasn't been populated for this post yet.
  useEffect(() => {
    if (postId && user) checkLiked(postId, user.id);
  }, [postId, user, checkLiked]);

  const scrollToComments = useCallback(() => {
    (commentsAnchorRef.current as unknown as HTMLElement | null)?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  useEffect(() => {
    if (!openComments || !selectedPost || !isDesktopShell) return;
    const frame = requestAnimationFrame(scrollToComments);
    return () => cancelAnimationFrame(frame);
  }, [openComments, selectedPost, isDesktopShell, scrollToComments]);

  const handleLike = useCallback(() => {
    if (!selectedPost) return;
    requireAuth(
      () => {
        const uid = useAuthStore.getState().user!.id;
        if (likedPosts.has(selectedPost.id)) {
          unlikePost(selectedPost.id, uid);
        } else {
          likePost(selectedPost.id, uid);
        }
      },
      { message: 'Sign in to like posts' }
    );
  }, [requireAuth, selectedPost, likedPosts, likePost, unlikePost]);

  const handleComment = useCallback(() => {
    if (isDesktopShell) {
      scrollToComments();
      return;
    }
    requireAuth(() => onRequestComments?.(), {
      message: 'Sign in to view comments',
      allowAnonymous: true,
    });
  }, [requireAuth, isDesktopShell, scrollToComments, onRequestComments]);

  const handleEdit = useCallback(() => {
    if (!selectedPost) return;
    requireAuth(() => router.push(`/post/create?editPostId=${selectedPost.id}`));
  }, [requireAuth, selectedPost]);

  const handleDelete = useCallback(() => {
    if (!selectedPost) return;
    requireAuth(async () => {
      const confirmed = await confirm({
        title: 'Delete post?',
        message: 'This action cannot be undone.',
        confirmLabel: 'Delete',
        destructive: true,
      });
      if (!confirmed) return;
      try {
        await deletePost(selectedPost.id, selectedPost.authorId, selectedPost.structureId);
        router.back();
      } catch {
        // Error surfaced via store
      }
    });
  }, [requireAuth, selectedPost, confirm, deletePost]);

  if (!selectedPost && !fetchFailed) {
    return <LoadingSpinner fullScreen message="Loading post..." />;
  }

  if (!selectedPost) {
    return (
      <EmptyState
        icon={Camera}
        title={accessDenied ? 'Post unavailable' : 'Post not found'}
        message={
          accessDenied
            ? "This post is private or you don't have access to it."
            : "This post may have been deleted or doesn't exist."
        }
      />
    );
  }

  const beforePostId = selectedPost.beforePostId;
  const checklistStructureId = selectedPost.structureId;
  const commentsCount = selectedPost.commentsCount ?? 0;
  const postCategory = normalizePostCategory(selectedPost.category);

  return (
    <View style={styles.container}>
      <PostCard
        post={selectedPost}
        fullImage
        isLiked={likedPosts.has(selectedPost.id)}
        currentUserId={user?.id}
        onLike={handleLike}
        onComment={handleComment}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {selectedPost.structureId && (
        <TouchableOpacity
          style={[styles.rowLink, styles.rowLinkFirst, { borderColor: colors.border }]}
          onPress={() => router.push(`/structure/${selectedPost.structureId}`)}
          accessibilityRole="button"
          accessibilityLabel="View property">
          <Home size={16} color={colors.textSecondary} />
          <Text style={[styles.rowLinkText, { color: colors.text }]} numberOfLines={1}>
            {selectedPost.address || 'View property'}
          </Text>
          <ChevronRight size={14} color={colors.textTertiary} />
        </TouchableOpacity>
      )}

      {selectedPost.checklistItemLabel &&
        (checklistStructureId ? (
          <TouchableOpacity
            style={[
              styles.rowLink,
              !selectedPost.structureId && styles.rowLinkFirst,
              { borderColor: colors.border },
            ]}
            onPress={() =>
              router.push(
                `/structure/${checklistStructureId}?item=${encodeURIComponent(selectedPost.checklistItemId ?? '')}`
              )
            }
            accessibilityRole="button"
            accessibilityLabel="View checklist item">
            <ClipboardCheck size={16} color={colors.textSecondary} />
            <Text style={[styles.rowLinkText, { color: colors.text }]} numberOfLines={1}>
              View checklist item
            </Text>
            <ChevronRight size={14} color={colors.textTertiary} />
          </TouchableOpacity>
        ) : (
          <View
            style={[
              styles.rowLink,
              !selectedPost.structureId && styles.rowLinkFirst,
              { borderColor: colors.border },
            ]}>
            <ClipboardCheck size={16} color={colors.textSecondary} />
            <Text style={[styles.rowLinkText, { color: colors.text }]} numberOfLines={1}>
              Checklist item
            </Text>
          </View>
        ))}

      {beforePostId && (
        <TouchableOpacity
          style={[
            styles.rowLink,
            !selectedPost.structureId && !selectedPost.checklistItemLabel && styles.rowLinkFirst,
            { borderColor: colors.border },
          ]}
          onPress={() => router.push(`/post/${beforePostId}`)}
          accessibilityRole="button"
          accessibilityLabel="View the before photo">
          <ArrowLeftRight size={16} color={colors.textSecondary} />
          <Text style={[styles.rowLinkText, { color: colors.text }]}>View the before photo</Text>
          <ChevronRight size={14} color={colors.textTertiary} />
        </TouchableOpacity>
      )}

      {user &&
        postCategory !== 'news' &&
        !selectedPost.beforePostId &&
        !selectedPost.beforeImageUrl && (
          <View style={styles.pairActions}>
            <Button
              title="Add an after photo"
              onPress={() =>
                router.push({
                  pathname: '/post/create',
                  params: {
                    structureId: selectedPost.structureId,
                    beforePostId: selectedPost.id,
                    ...(selectedPost.checklistItemId
                      ? { checklistItemId: selectedPost.checklistItemId }
                      : {}),
                  },
                })
              }
              variant="outline"
              size="sm"
              leftIcon={Camera}
              fullWidth
            />
          </View>
        )}

      {isDesktopShell && (
        <View ref={commentsAnchorRef}>
          <InlineComments postId={selectedPost.id} commentsCount={commentsCount} />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  rowLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.smd - 1,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.smd + 1,
    borderTopWidth: 1,
  },
  // The first row opens with its own rule now; the post card above no
  // longer draws one on the detail view.
  rowLinkFirst: {
    marginTop: SPACING.smd,
  },
  rowLinkText: {
    ...TYPOGRAPHY.label,
    flex: 1,
  },
  pairActions: {
    paddingHorizontal: SPACING.md,
    marginTop: SPACING.smd,
  },
});
